import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Document } from "@/types/database";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { logActivity } from "@/lib/activityLogger";

export function useDocuments(hostId?: string) {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["documents", activeOrgId, hostId],
    enabled: !!activeOrgId,
    queryFn: async () => {
      let query = supabase
        .from("documents")
        .select("*")
        .eq("organization_id", activeOrgId!)
        .order("uploaded_at", { ascending: false });

      if (hostId) {
        query = query.eq("host_id", hostId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Document[];
    },
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({
      file,
      hostId,
      name,
      expiresAt,
    }: {
      file: File;
      hostId: string;
      name: string;
      expiresAt?: string;
    }) => {
      if (!activeOrgId) throw new Error("No active organization");

      // Upload file to Supabase Storage
      const filePath = `documents/${hostId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("host-documents")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("host-documents")
        .getPublicUrl(filePath);

      // Create document record
      const { data, error } = await supabase
        .from("documents")
        .insert({
          host_id: hostId,
          organization_id: activeOrgId,
          name,
          file_name: file.name,
          file_url: urlData.publicUrl,
          expires_at: expiresAt,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["documents", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: "Document uploaded", description: "Your document has been uploaded for review." });
      if (activeOrgId) {
        logActivity({
          orgId: activeOrgId, actionType: "document_uploaded",
          entityType: "document", entityId: data.id,
          metadata: { document_name: data.name, host_id: data.host_id },
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    },
  });
}

export const REGISTRATION_CERT_NAME = "BC STR Registration Certificate";

/** Returns the most recent registration certificate document for a host. */
export function useRegistrationCertificate(hostId?: string) {
  const { activeOrgId } = useAuth();

  return useQuery({
    queryKey: ["registration_cert", activeOrgId, hostId],
    enabled: !!activeOrgId && !!hostId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("organization_id", activeOrgId!)
        .eq("host_id", hostId!)
        .eq("name", REGISTRATION_CERT_NAME)
        .order("uploaded_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as Document | null;
    },
  });
}

export function useUpdateDocumentStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeOrgId } = useAuth();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { data, error } = await supabase
        .from("documents")
        .update({ status })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["documents", activeOrgId] });
      queryClient.invalidateQueries({ queryKey: ["activity_feed", activeOrgId] });
      toast({ title: "Document updated", description: "Document status has been updated." });
      if (activeOrgId) {
        const actionType = variables.status === "approved" ? "document_approved"
          : variables.status === "rejected" ? "document_rejected"
          : "document_uploaded";
        logActivity({
          orgId: activeOrgId, actionType,
          entityType: "document", entityId: variables.id,
          metadata: { document_name: data.name },
        });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
}
